import React from 'react';

const STATUS_CLASS = {
  normal: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
  dikkat: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
  risk: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300',
  kritik: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-700 dark:bg-rose-900/20 dark:text-rose-300'
};

function Badge({ label, value, tone = 'normal' }) {
  return (
    <span className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold ${STATUS_CLASS[tone] || STATUS_CLASS.normal}`}>
      <span>{label}</span>
      <span className="font-black">{value}</span>
    </span>
  );
}

function formatDate(value) {
  if (!value) return 'Bilinmiyor';
  try {
    return new Date(value).toLocaleString('tr-TR');
  } catch (_) {
    return 'Bilinmiyor';
  }
}

export default function OnboardingSmokeGateCard({ data }) {
  const status = String(data?.status || 'dikkat').toLowerCase();
  const summary = data?.summary || {};
  const profileBreakdown = Array.isArray(data?.profileBreakdown) ? data.profileBreakdown : [];
  const source = data?.source || {};
  const anomaliesPreview = Array.isArray(data?.anomaliesPreview) ? data.anomaliesPreview : [];
  const failuresPreview = Array.isArray(data?.failuresPreview) ? data.failuresPreview : [];

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-[#101922]">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-black text-slate-900 dark:text-white">Onboarding Smoke Gate</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Son uçtan uca onboarding doğrulama raporu
          </p>
        </div>
        <div className="text-right text-xs text-slate-500 dark:text-slate-400">
          <div>Kaynak: <code>{String(source.reportPath || source.fileName || 'Bilinmiyor')}</code></div>
          <div>Bitiş: {formatDate(summary.finishedAt)}</div>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-2 overflow-x-auto pb-1">
        <Badge label="Durum" value={status.toUpperCase()} tone={status} />
        <Badge label="Vaka" value={Number(summary.caseCount || 0).toLocaleString('tr-TR')} tone={status} />
        <Badge label="Geçen" value={Number(summary.passedCaseCount || 0).toLocaleString('tr-TR')} tone={Number(summary.failedCaseCount || 0) > 0 ? 'dikkat' : 'normal'} />
        <Badge label="Anomali" value={Number(summary.anomalyCaseCount || 0).toLocaleString('tr-TR')} tone={Number(summary.anomalyCaseCount || 0) > 0 ? 'dikkat' : 'normal'} />
        <Badge label="Failure" value={Number(summary.failedCaseCount || 0).toLocaleString('tr-TR')} tone={Number(summary.failedCaseCount || 0) > 0 ? 'kritik' : 'normal'} />
      </div>

      <p className="mb-3 text-xs text-slate-600 dark:text-slate-300">
        {String(data?.note || 'Smoke özeti bekleniyor.')}
      </p>

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h4 className="text-xs font-black uppercase tracking-wide text-slate-700 dark:text-slate-200">Profil Özeti</h4>
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              Scale: {(Array.isArray(summary.servingSizes) ? summary.servingSizes : []).join(', ') || 'Yok'}
            </span>
          </div>
          <div className="space-y-2">
            {profileBreakdown.length ? profileBreakdown.map((row) => (
              <div key={row.profileKey} className="flex items-center justify-between gap-3 rounded-md bg-slate-50 px-3 py-2 text-xs dark:bg-slate-900/50">
                <span className="font-semibold text-slate-700 dark:text-slate-200">{String(row.profileKey || '-')}</span>
                <span className="text-slate-500 dark:text-slate-400">
                  {Number(row.okCount || 0).toLocaleString('tr-TR')}/{Number(row.totalCases || 0).toLocaleString('tr-TR')} geçerli
                </span>
              </div>
            )) : (
              <p className="text-xs text-slate-500 dark:text-slate-400">Profil kırılımı henüz yok.</p>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
          <h4 className="mb-2 text-xs font-black uppercase tracking-wide text-slate-700 dark:text-slate-200">Kısa Uyarı Özeti</h4>
          {failuresPreview.length ? (
            <ul className="space-y-2 text-xs text-rose-700 dark:text-rose-300">
              {failuresPreview.map((item, index) => (
                <li key={`failure-${index}`} className="rounded-md bg-rose-50 px-3 py-2 dark:bg-rose-900/20">
                  {String(item?.message || item?.error || 'Smoke failure')}
                </li>
              ))}
            </ul>
          ) : anomaliesPreview.length ? (
            <ul className="space-y-2 text-xs text-amber-700 dark:text-amber-300">
              {anomaliesPreview.map((item, index) => (
                <li key={`anomaly-${index}`} className="rounded-md bg-amber-50 px-3 py-2 dark:bg-amber-900/20">
                  {String(item?.message || item?.reason || item?.profileKey || 'Smoke anomali')}
                </li>
              ))}
            </ul>
          ) : (
            <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">
              Son raporda anomali veya failure görünmüyor.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
